// Shared state and parts of the Nutrition hub (refonte navigation 21/10, Task 4). One session store for the four rubriques
// (Repas · Recettes · Courses · Journal): the week plan, the recipes sent to the shopping list, the bought items, favourites,
// the journal and its coach sharing. Session memory only (survives navigation inside the prototype, not a reload).
// FICTITIOUS demo: no estimate, no calorie or macro value.
import { useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import { Check, Utensils } from "lucide-react";
import { CustomSheet } from "./sheet";
import { BOUGHT, FAVORITES, JOURNAL, JOURNAL_COMPLETE, PLAN, RECIPES, type Journal, type MealId, type PlanEntry, type Recipe } from "./nutrition-fixtures";

export type Plan = Record<string, Partial<Record<MealId, PlanEntry>>>;
export interface NState {
  plan: Plan;
  /** Recipes sent to the shopping list from their page, with the portions chosen there. */
  extras: Record<string, number>;
  bought: string[];
  favs: string[];
  journal: Journal;
  complete: string[];
  share: boolean;
  toast: string | null;
}

let state: NState = {
  plan: structuredClone(PLAN ?? {}), extras: {}, bought: [...BOUGHT], favs: [...FAVORITES],
  journal: structuredClone(JOURNAL), complete: [...JOURNAL_COMPLETE], share: false, toast: null,
};
const listeners = new Set<() => void>();
let toastTimer = 0;
export const nutri = {
  get: () => state,
  set(patch: Partial<NState>) { state = { ...state, ...patch }; listeners.forEach(l => l()); },
  toast(text: string) {
    window.clearTimeout(toastTimer);
    nutri.set({ toast: text });
    toastTimer = window.setTimeout(() => nutri.set({ toast: null }), 2400);
  },
  setEntry(day: string, meal: MealId, entry: PlanEntry | null) {
    const plan = structuredClone(state.plan);
    plan[day] = { ...plan[day] };
    if (entry) plan[day]![meal] = entry; else delete plan[day]![meal];
    nutri.set({ plan });
  },
  toggleFav(id: string) { nutri.set({ favs: state.favs.includes(id) ? state.favs.filter(x => x !== id) : [...state.favs, id] }); },
};
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useNutri = () => useSyncExternalStore(subscribe, nutri.get, nutri.get);

export const recipeOf = (id: string): Recipe => RECIPES.find(r => r.id === id) ?? RECIPES[0]!;
export const fmtQty = (n: number) => (Math.round(n * 10) / 10).toLocaleString("fr-FR");
const COUNTABLE = ["pièce", "tranche", "bouquet"];
/** Quantity + unit with the French plural of countable units (« 1 pièce », « 3 pièces »). */
export const fmtAmount = (n: number, unit: string) => {
  const base = unit.replace(/s$/, "");
  return `${fmtQty(n)} ${COUNTABLE.includes(base) ? (Math.round(n * 10) / 10 >= 2 ? `${base}s` : base) : unit}`;
};
export const recipeHref = (rub: string, id: string) => `#/nutrition?r=${rub}&recette=${id}`;

/** Shopping item: same ingredient + same unit summed over the planned recipes and the recipes added from their page. */
export interface CourseItem { key: string; name: string; unit: string; qty: number; from: string[] }
const collect = (plan: Plan, extras: Record<string, number>) => {
  const map = new Map<string, CourseItem>();
  const add = (r: Recipe, portions: number) => {
    const k = portions / r.portions;
    for (const ing of r.ingredients) {
      const key = `${ing.name}|${ing.unit}`;
      const cur = map.get(key) ?? { key, name: ing.name, unit: ing.unit, qty: 0, from: [] };
      cur.qty += ing.qty * k;
      if (!cur.from.includes(r.name)) cur.from.push(r.name);
      map.set(key, cur);
    }
  };
  for (const meals of Object.values(plan)) for (const e of Object.values(meals)) if (e && "recipe" in e) add(recipeOf(e.recipe), e.portions);
  for (const [id, portions] of Object.entries(extras)) add(recipeOf(id), portions);
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "fr"));
};
export const coursesOf = (s: NState) => collect(s.plan, s.extras);
export const planSignature = (plan: Plan) => collect(plan, {}).map(i => `${i.key}|${fmtQty(i.qty)}`).join(";");
/** Signature of the list built from the initial plan: the base « Plan modifié » compares against. */
export const INITIAL_SIGNATURE = planSignature(structuredClone(PLAN ?? {}));

/** Placeholder-or-photo thumbnail (honest placeholder when a recipe has no demo photo). */
export function Thumb({ photo, className, icon }: { photo?: string; className: string; icon?: ReactNode }) {
  return photo ? <img className={className} src={photo} alt="" loading="lazy" />
    : <span className={`${className} ccn-ph`} aria-hidden="true">{icon ?? <Utensils size={20} strokeWidth={1.6} />}</span>;
}

/** Drawer of the hub: the shared CustomSheet (handle, no cross, focus trap, Escape, swipe). */
export function NutriSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <CustomSheet title={title} onClose={onClose} overlayClassName="cc-nutrition-overlay" className="cc-nutrition-sheet ccn-sheet">{children}</CustomSheet>;
}

/** Day slider of Repas and Journal: seven equal days, a sliding ink indicator, a state mark per day. */
export function DayStrip({ days, value, onChange, today, mark, label }: {
  days: { key: string; letter: string; num: number; long: string }[]; value: string; onChange: (key: string) => void;
  today: string; mark: (key: string) => "done" | "dot" | null; label: string;
}) {
  const i = Math.max(0, days.findIndex(d => d.key === value));
  return <div className="ccn-days" role="tablist" aria-label={label} style={{ ["--i" as string]: i }}>
    <span className="ccn-days-ind" aria-hidden="true"><i /></span>
    {days.map(d => { const m = mark(d.key); return <button key={d.key} type="button" role="tab" aria-selected={d.key === value}
      aria-label={`${d.long}${d.key === today ? ", aujourd'hui" : ""}`} className="ccn-day" data-active={d.key === value || undefined} data-today={d.key === today || undefined}
      onClick={() => onChange(d.key)}>
      <span className="ccn-day-l">{d.letter}</span><span className="ccn-day-n">{d.num}</span>
      <span className="ccn-day-m" data-m={m ?? undefined}>{m === "done" && <Check size={9} strokeWidth={3.4} />}</span>
    </button>; })}
  </div>;
}

/** Content slide when the selected day changes (direction-aware, reduced motion: short fade). */
export function useDaySwap(key: string, order: string[]) {
  const ref = useRef<HTMLDivElement>(null);
  const prev = useRef(key);
  useLayoutEffect(() => {
    const el = ref.current; if (!el || prev.current === key) return;
    const dir = order.indexOf(key) >= order.indexOf(prev.current) ? 1 : -1;
    prev.current = key;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.animate(reduce ? [{ opacity: .4 }, { opacity: 1 }] : [{ opacity: 0, transform: `translateX(${dir * 18}px)` }, { opacity: 1, transform: "none" }],
      { duration: reduce ? 120 : 320, easing: "cubic-bezier(.22, 1, .36, 1)" });
  }, [key]);
  return ref;
}
